/**
 * Predicted Lifetime Value (pLTV) scoring function — JOIN FUNNEL VARIANT (Model B).
 *
 * The join funnel runs a DIFFERENT pLTV model than walkmore (Model A) so the two
 * can be compared head-to-head — which model drives better Meta Value
 * Optimization. The public interface (exports, params, return contract) is kept
 * IDENTICAL to Model A so every call site is interchangeable; only the base
 * values, multipliers, and output range differ.
 *
 * Model B design philosophy: "flatter / conservative".
 *   - Smaller spread between plan base values (less plan-driven separation).
 *   - Multipliers compressed toward 1.0 (roughly half the deviation of Model A),
 *     so individual quiz signals move the score less aggressively.
 *   - Tighter output range ($15–$150) than Model A ($10–$200).
 *
 * Computes a dollar-denominated LTV estimate from quiz signals, plan selection,
 * card funding type, and upsell acceptance. The score is sent to Meta as the
 * Purchase event `value` so Value Optimization targets high-LTV lookalikes.
 */

/** Identifies which pLTV model this funnel runs (for analytics/debugging). */
export const LTV_MODEL = 'join_b_flat';

// ── Dynamic multiplier config (served by backend, cached per session) ───────

export interface LTVMultiplierConfig {
    version: number;
    baseLTV: Record<string, number>;
    multipliers: Record<string, number>;
    computedAt: string | null;
}

/** Hardcoded defaults — used when backend multiplier API is unavailable. */
export const DEFAULT_MULTIPLIER_CONFIG: LTVMultiplierConfig = {
    version: 0,
    baseLTV: {
        // Flatter spread than Model A (22 / 38 / 52)
        '1_week_trial': 30,
        '4_week': 40,
        '12_week': 50,
    },
    multipliers: {
        // Tier 1: Highest signal (compressed toward 1.0 vs Model A)
        targetDate_set: 1.12,
        commitment_absolutely: 1.08,
        commitment_not_sure: 0.85,
        mainBarrier_uncertainty: 0.88,
        mainBarrier_no_results_fast: 0.90,
        primaryGoal_clothes_fit: 0.92,

        // Tier 2: Strong supporting
        motivation_guidance: 1.05,
        motivation_gift_cards: 0.95,
        walkingHistory_lapsed: 1.05,
        walkingHistory_never: 0.92,
        cortisolLoop: 1.05,

        // Tier 3: Weaker signals
        ageGroup_40_49: 1.02,
        activityLevel_light: 1.02,
        activityLevel_sedentary: 0.95,
        rewardPreference_nike: 1.02,
        weightToLose_sweetSpot: 1.02,
        weightToLose_high: 0.95,
        weightToLose_low: 0.95,

        // Payment
        cardFunding_prepaid: 0.90,
        cardFunding_debit: 0.97,

        // Upsell
        hasCoachingUpsell: 1.20,
        hasFeatureUnlock: 1.08,
    },
    computedAt: null,
};

// ── LTV scoring ─────────────────────────────────────────────────────────────

export interface PredictLTVParams {
    planType: string;
    // Tier 1 signals
    targetDate: string | null;
    commitment: string | null;        // 'absolutely' | 'think_so' | 'not_sure'
    primaryGoal: string | null;
    mainBarrier: string | null;
    // Tier 2 signals
    motivation: string | null;
    walkingHistory: string | null;
    sleepQuality: string | null;
    stressSymptoms: string[];
    // Tier 3 signals
    ageGroup: string | null;
    activityLevel: string | null;
    rewardPreference: string | null;
    weightToLosePercent: number | null; // (current - goal) / current × 100
    // Payment signal
    cardFunding?: string | null;       // 'credit' | 'debit' | 'prepaid' | 'unknown'
    // Upsell signals
    hasCoachingUpsell?: boolean;
    hasFeatureUnlock?: boolean;
}

/**
 * Compute predicted LTV (Model B — flatter/conservative).
 *
 * @param params  Quiz + payment signals
 * @param config  Optional dynamic multiplier config from backend API.
 *                Falls back to DEFAULT_MULTIPLIER_CONFIG when omitted.
 */
export function predictLTV(params: PredictLTVParams, config?: LTVMultiplierConfig): number {
    const c = config || DEFAULT_MULTIPLIER_CONFIG;
    const m = c.multipliers;
    let score = c.baseLTV[params.planType] ?? c.baseLTV['4_week'];

    // ── Tier 1: Highest signal ──────────────────────────────────────────────
    if (params.targetDate) score *= m.targetDate_set;
    if (params.commitment === 'absolutely') score *= m.commitment_absolutely;
    if (params.commitment === 'not_sure') score *= m.commitment_not_sure;
    if (params.mainBarrier === 'uncertainty') score *= m.mainBarrier_uncertainty;
    if (params.mainBarrier === 'no_results_fast') score *= m.mainBarrier_no_results_fast;
    if (params.primaryGoal === 'clothes_fit') score *= m.primaryGoal_clothes_fit;

    // ── Tier 2: Strong supporting ───────────────────────────────────────────
    if (params.motivation === 'guidance') score *= m.motivation_guidance;
    if (params.motivation === 'gift_cards') score *= m.motivation_gift_cards;
    if (params.walkingHistory === 'lapsed') score *= m.walkingHistory_lapsed;
    if (params.walkingHistory === 'never') score *= m.walkingHistory_never;
    // Cortisol loop: drained sleep + multiple stress symptoms = acute pain = higher motivation
    if (params.sleepQuality === 'drained' && params.stressSymptoms.length >= 2) {
        score *= m.cortisolLoop;
    }

    // ── Tier 3: Weaker signals ──────────────────────────────────────────────
    if (params.ageGroup === '40-49') score *= m.ageGroup_40_49;
    if (params.activityLevel === 'light') score *= m.activityLevel_light;
    if (params.activityLevel === 'sedentary') score *= m.activityLevel_sedentary;
    if (params.rewardPreference === 'nike') score *= m.rewardPreference_nike;
    if (params.weightToLosePercent !== null) {
        if (params.weightToLosePercent >= 10 && params.weightToLosePercent <= 20) {
            score *= m.weightToLose_sweetSpot; // sweet spot
        } else if (params.weightToLosePercent > 30) {
            score *= m.weightToLose_high; // may feel overwhelmed
        } else if (params.weightToLosePercent < 5) {
            score *= m.weightToLose_low; // low urgency
        }
    }

    // ── Card funding signal ─────────────────────────────────────────────────
    if (params.cardFunding === 'prepaid') score *= m.cardFunding_prepaid;
    if (params.cardFunding === 'debit') score *= m.cardFunding_debit;

    // ── Upsell multipliers (only ~5-10% of users) ──────────────────────────
    if (params.hasCoachingUpsell) score *= m.hasCoachingUpsell;
    if (params.hasFeatureUnlock) score *= m.hasFeatureUnlock;

    // Floor $15 / ceiling $150 — tighter range than Model A keeps Model B's
    // value-optimization signal lower-variance.
    return Math.round(Math.min(Math.max(score, 15), 150));
}
